import { useMemo } from 'react';
import styled from '@emotion/styled';
import type { Chat, ChatCitationType } from '@trinity/chat';
import { NivoBarChart } from '@trinity/chat-components/components/Chat.Charts/Bar';
import { NivoMultipleBarChart } from '@trinity/chat-components/components/Chat.Charts/MultipleBar';
import { NivoPieChart } from '@trinity/chat-components/components/Chat.Charts/Pie';
import { NivoStackedBarChart } from '@trinity/chat-components/components/Chat.Charts/StackedBar';
import { BasicMarkdown } from '@trinity/chat-components/components/Markdown/Markdown';
import { AnalysisTable } from '@trinity/chat-components/components/QuantAnalysis';
import { KPIWidget } from '@trinity/chat-components/components/QuantAnalysis/KPIWidget';
import { useOptionsColors } from '@trinity/chat-components/containers/AppTheming/useOptionsColors';
import { MessageCitationMaybeLink } from './Citation.MaybeLink';

type Props = {
  snippet: Chat.CitationSnippet<ChatCitationType.Dashboard>;
  link?: string | null;
};

/**
 * Renders a dashboard citation (widget or rich text)
 * - For widgets: Renders actual chart/KPI components
 * - For rich text: Renders markdown content
 */
export const DashboardCitation = ({ snippet, link }: Props) => {
  const chartColors = useMemo(() => ['#03045E', '#023E8A', '#0096C7', '#00B4D8', '#48CAE4', '#90E0EF', '#CAF0F8', '#FFF0F3', '#FF8FA3', '#FF758F', '#FF4D6D', '#C9184A', '#800F2F', '#590D22'], []);
  const metadata = snippet.metadata;

  const renderContent = useMemo(() => {
    // Rich text: render as markdown
    if (metadata.itemType === 'rich-text') {
      return (
        <ContentWrapper>
          {snippet.text && <BasicMarkdown>{snippet.text}</BasicMarkdown>}
        </ContentWrapper>
      );
    }

    // Widget: render the actual chart/KPI component
    if (metadata.itemType === 'widget' && metadata.widgetData) {
      const chart = metadata.widgetData;

      switch (chart.type) {
        case 'bar': {
          const barChartColors = chart.chart.data.map((_, i) => chartColors[i % chartColors.length]);
          return (
            <ChartWrapper>
              <ChartTitle>{chart.chart.title}</ChartTitle>
              <NivoBarChart chart={chart.chart} colors={barChartColors} />
            </ChartWrapper>
          );
        }

        case 'stacked-bar': {
          const barChartColors = chart.chart.data.map((_, i) => chartColors[i % chartColors.length]);
          return (
            <ChartWrapper>
              <ChartTitle>{chart.chart.title}</ChartTitle>
              <NivoStackedBarChart chart={chart.chart} colors={barChartColors} />
            </ChartWrapper>
          );
        }

        case 'pie': {
          const pieChartColors = chart.chart.data.map((_, i) => chartColors[i % chartColors.length]);
          return (
            <ChartWrapper>
              <ChartTitle>{chart.chart.title}</ChartTitle>
              <NivoPieChart chart={chart.chart} colors={pieChartColors} />
            </ChartWrapper>
          );
        }

        case 'multiple-clustered-bar':
        case 'multiple-stacked-bar': {
          const allKeys = new Set<string>();
          chart.chart.data.forEach(series => {
            series.data.forEach(d => allKeys.add(d.x));
          });
          const keys = Array.from(allKeys);
          const multipleBarChartColors = keys.map((_, i) => chartColors[i % chartColors.length]);

          return (
            <ChartWrapper>
              <ChartTitle>{chart.chart.title}</ChartTitle>
              <NivoMultipleBarChart
                chart={chart.chart}
                colors={multipleBarChartColors}
                isStacked={chart.type === 'multiple-stacked-bar'}
              />
            </ChartWrapper>
          );
        }

        case 'kpi-widget': {
          return (
            <KPIWrapper>
              <KPIWidget
                title={chart.title}
                backgroundColor={chart.backgroundColor}
                values={chart.values}
                labels={chart.labels}
                insights={chart.insights}
              />
            </KPIWrapper>
          );
        }

        case 'data-table': {
          return (
            <TableWrapper>
              <AnalysisTable
                data={chart.data}
                headerBackgroundColor={chart.headerColor}
                initialSortConfig={chart.sortConfig}
                disableSorting={false}
                disableSelection={true}
              />
            </TableWrapper>
          );
        }

        default:
          return (
            <ContentWrapper>
              <NoContentMessage>Widget type not supported for preview</NoContentMessage>
            </ContentWrapper>
          );
      }
    }

    return (
      <ContentWrapper>
        <NoContentMessage>No content available</NoContentMessage>
      </ContentWrapper>
    );
  }, [metadata, snippet.text, chartColors]);

  return (
    <StyledRoot>
      {renderContent}
      {link && (
        <ViewButtonWrapper>
          <MessageCitationMaybeLink to={link}>
            <View>{`View >`}</View>
          </MessageCitationMaybeLink>
        </ViewButtonWrapper>
      )}
    </StyledRoot>
  );
};

const StyledRoot = styled.div`
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ContentWrapper = styled.div`
  min-height: 100px;
`;

const ChartWrapper = styled.div`
  min-height: 240px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px solid var(--gray-xl, #e0e0e0);
  border-radius: 8px;
  background-color: #fff;
`;

const ChartTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.palette.gray.dark1};
  margin: 0;
  padding: 0;
`;

const KPIWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const NoContentMessage = styled.div`
  color: ${props => props.theme.palette.gray.dark1};
  font-style: italic;
  text-align: center;
  padding: 16px;
`;

const ViewButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-top: 8px;
`;

const View = styled.div`
  font-size: 13px;
  font-family: ${props => props.theme.fonts.semiBold};
`;
